import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Alert,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import SubscriptionModal from "./SuscripcionModal";

const BillingDashboard = ({ data }) => {
  const [openModal, setOpenModal] = useState(false);
  const [amountToPay, setAmountToPay] = useState(0);

  const {
    saldo = 0,
    bloqueado = false,
    costoDiario = 0,
    movimientos = [],
    desglose = {},
    totalComision = 0,
  } = data?.billing || {};

  const opcionesRecarga = [
    { meses: 1, descuento: 0 },
    { meses: 2, descuento: 0.03 },
    { meses: 3, descuento: 0.1 },
    { meses: 4, descuento: 0.12 },
    { meses: 6, descuento: 0.15 },
    { meses: 12, descuento: 0.25 },
  ];

  const diasRestantes = costoDiario > 0 ? Math.floor(saldo / costoDiario) : "—";

  const montoDiarioCalculado = useMemo(() => {
    return ["propiedad", "habitacion", "ota", "mantenimiento"].reduce(
      (acc, key) => {
        const item = desglose[key];
        if (item?.cantidad && item?.unitario)
          return acc + item.cantidad * item.unitario;
        return acc;
      },
      0,
    );
  }, [desglose]);

  const softCardSx = {
    borderRadius: 4,
    p: { xs: 1.5, sm: 2 },
    bgcolor: "rgba(255,255,255,0.70)",
    backdropFilter: "blur(6px)",
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.10)",
  };

  return (
    <Box
      sx={{
        bgcolor: "rgba(245, 240, 244, 0.42)", // ✅ igual que ReservationForm
        p: { xs: 2, sm: 4 },
        borderRadius: 4,
        minHeight: "100vh",
        backdropFilter: "blur(3px)",
      }}
    >
      {bloqueado && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          Tu cuenta está <strong>bloqueada por saldo insuficiente</strong>.
          Recargá para reactivarla.
        </Alert>
      )}

      {/* ✅ CARD RESUMEN */}
      <Card sx={{ ...softCardSx, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Resumen de Facturación
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={1.2} sx={{ fontSize: "0.95rem", mb: 1 }}>
            <Grid item xs={6}>
              Saldo disponible
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography
                component="span"
                sx={{
                  fontWeight: 900,
                  color: saldo < 0 ? "#d32f2f" : "#2e7d32",
                }}
              >
                ${saldo.toFixed(2)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              Costo diario aplicado
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography component="span" sx={{ fontWeight: 800 }}>
                ${costoDiario.toFixed(2)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              Días restantes estimados
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography component="span" sx={{ fontWeight: 800 }}>
                {diasRestantes}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              Comisión Ubika este mes
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography component="span" sx={{ fontWeight: 800 }}>
                ${totalComision.toFixed(2)}
              </Typography>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
            Precios aplicados según tu uso
          </Typography>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              mt: 1.5,
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.06)",
              bgcolor: "rgba(255,255,255,0.65)",
            }}
          >
            <Table size="small">
              <TableHead sx={{ bgcolor: "rgba(144,58,242,0.10)" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Elemento</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900 }}>
                    Cantidad
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900 }}>
                    Precio diario unitario
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {["propiedad", "habitacion", "ota", "mantenimiento"].map(
                  (key) => (
                    <TableRow key={key} hover>
                      <TableCell>
                        {key === "propiedad"
                          ? "Propiedades"
                          : key === "habitacion"
                            ? "Habitaciones"
                            : key === "ota"
                              ? "Canales OTA"
                              : "Mantenimiento servidor"}
                      </TableCell>
                      <TableCell align="right">
                        {desglose[key]?.cantidad ?? "—"}
                      </TableCell>
                      <TableCell align="right">
                        {desglose[key]?.unitario !== undefined
                          ? `$${desglose[key].unitario.toFixed(2)}`
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              mt: 2,
              px: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Monto diario estimado según desglose:
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">
              ${montoDiarioCalculado.toFixed(2)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* ✅ RECARGA INTELIGENTE */}
      <Card sx={{ ...softCardSx, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Ahorra recargando varios meses
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body1" sx={{ color: "rgba(0,0,0,0.72)" }}>
            Cuantos más meses recargues, menor será el costo por mes. Aprovechá
            los descuentos por anticipado y asegurá tu acceso sin
            interrupciones.
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            {opcionesRecarga.map(({ meses, descuento }) => {
              const dias = meses * 30;
              const precioNormal = costoDiario * dias;
              const ahorro = +(precioNormal * descuento).toFixed(2);
              const montoFinal = +(precioNormal - ahorro).toFixed(2);
              const saldoReal = +precioNormal.toFixed(2);

              const isPopular = meses === 3;

              return (
                <Grid item xs={12} sm={6} md={4} key={meses}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      borderColor: isPopular
                        ? "#EE964B"
                        : "rgba(144,58,242,0.35)",
                      background: isPopular
                        ? "linear-gradient(180deg, #FFF7ED 0%, #FFFFFF 100%)"
                        : "linear-gradient(180deg, rgba(144,58,242,0.06) 0%, rgba(255,255,255,0.95) 100%)",
                      transition: "0.22s ease",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 22px rgba(0,0,0,0.12)",
                      },
                    }}
                    onClick={() => {
                      setAmountToPay(montoFinal);
                      setOpenModal(true);
                    }}
                  >
                    <Box sx={{ mb: 1 }}>
                      {descuento > 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: "line-through",
                            color: "rgba(0,0,0,0.45)",
                            fontWeight: 700,
                          }}
                        >
                          ${saldoReal}
                        </Typography>
                      )}

                      <Typography variant="h4" fontWeight={900} color="primary">
                        ${montoFinal}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          fontWeight: 800,
                          color: "rgba(0,0,0,0.72)",
                        }}
                      >
                        {meses} {meses === 1 ? "mes" : "meses"}
                      </Typography>
                    </Box>

                    {isPopular && (
                      <Chip
                        label="★ Más popular"
                        size="small"
                        sx={{
                          mb: 1,
                          bgcolor: "rgba(238,150,75,0.18)",
                          color: "#b85f12",
                          border: "1px solid rgba(238,150,75,0.35)",
                          fontWeight: 900,
                        }}
                      />
                    )}

                    {descuento > 0 ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 900, color: "#EE964B", mt: 1 }}
                        >
                          Ahorro: ${ahorro} ({(descuento * 100).toFixed(0)}%
                          OFF)
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            fontWeight: 700,
                            color: "rgba(0,0,0,0.72)",
                          }}
                        >
                          🎁 ¡Te regalamos ${ahorro} de saldo!
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(0,0,0,0.55)" }}
                      >
                        Sin descuento
                      </Typography>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* ✅ MOVIMIENTOS */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
        Movimientos recientes
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.06)",
          bgcolor: "rgba(255,255,255,0.70)",
          backdropFilter: "blur(6px)",
          boxShadow: "0 12px 30px rgba(0,0,0,0.10)",
        }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: "rgba(144,58,242,0.12)" }}>
            <TableRow>
              {["Fecha", "Tipo", "Monto", "Saldo antes", "Saldo después"].map(
                (h) => (
                  <TableCell key={h} sx={{ fontWeight: 900, color: "#1d1d1f" }}>
                    {h}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {movimientos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay movimientos registrados aún.
                </TableCell>
              </TableRow>
            ) : (
              movimientos.slice().map((item, i) => {
                const isRecarga = item.amount > 0;
                return (
                  <TableRow key={i} hover>
                    <TableCell>
                      {(() => {
                        const d = new Date(item.date);
                        const day = String(d.getUTCDate()).padStart(2, "0");
                        const month = String(d.getUTCMonth() + 1).padStart(
                          2,
                          "0",
                        );
                        const year = d.getUTCFullYear();
                        return `${day}/${month}/${year}`;
                      })()}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={isRecarga ? "Recarga" : "Débito"}
                        size="small"
                        sx={{
                          fontWeight: 900,
                          bgcolor: isRecarga
                            ? "rgba(46,125,50,0.12)"
                            : "rgba(211,47,47,0.12)",
                          color: isRecarga ? "#2e7d32" : "#d32f2f",
                          border: `1px solid ${
                            isRecarga
                              ? "rgba(46,125,50,0.25)"
                              : "rgba(211,47,47,0.25)"
                          }`,
                        }}
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 900,
                        color: isRecarga ? "#2e7d32" : "#d32f2f",
                      }}
                    >
                      {isRecarga ? "+" : "-"}${Math.abs(item.amount).toFixed(2)}
                    </TableCell>

                    <TableCell>
                      {item.saldoAntes !== undefined
                        ? `$${item.saldoAntes.toFixed(2)}`
                        : "—"}
                    </TableCell>

                    <TableCell>
                      {item.saldoDespues !== undefined
                        ? `$${item.saldoDespues.toFixed(2)}`
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SubscriptionModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        amount={amountToPay}
      />
    </Box>
  );
};

export default BillingDashboard;
